import { EnvironmentOutlined } from '@ant-design/icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { Modal, Table } from 'antd'
import classNames from 'classnames'
import { Suspense, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import Loader from '@/components/Atoms/Loader/Loader'
import {
  type DeliveryAddressFormType,
  deliveryAddressSchema,
} from '@/components/EntityCreationModals/DeliveryAddressCreationModal/DeliveryAddressCreationModal.config'
import { DEFAULT_PAGE_SIZE } from '@/constants'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import { DeliveryAddressInterface } from '@/types'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import { safeLazy } from '@/utils/app/safeLazy'
import { getPageSizeChanger } from '@/utils/table/getPageSizeChanger'

import { getColumns } from './DeliveryAddresses.config'
import cn from './DeliveryAddresses.module.scss'

const DeliveryAddressCreationModal = safeLazy(
  () =>
    import(
      '@/components/EntityCreationModals/DeliveryAddressCreationModal/DeliveryAddressCreationModal'
    )
)

let preloadPromise: Promise<
  typeof import('@/components/EntityCreationModals/DeliveryAddressCreationModal/DeliveryAddressCreationModal')
> | null = null
function preloadModal() {
  if (!preloadPromise) {
    preloadPromise = import(
      '@/components/EntityCreationModals/DeliveryAddressCreationModal/DeliveryAddressCreationModal'
    )
  }
  return preloadPromise
}

function DeliveryAddresses() {
  const [addresses, setAddresses] = useState<DeliveryAddressInterface[]>([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [editingId, setEditingId] = useState('')

  const selectedOrganisation = useUserInfoStore(
    (state) => state.selectedOrganisation
  )
  const organisation = useUserInfoStore((state) => state.organisation)
  const { code } = useParams()
  const isSelectedOrganisation =
    Object.keys(selectedOrganisation).length > 0 &&
    String(selectedOrganisation.code) == code
  const org = isSelectedOrganisation ? selectedOrganisation : organisation

  const [modal, contextHolder] = Modal.useModal()

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<DeliveryAddressFormType>({
    shouldFocusError: false,
    resolver: zodResolver(deliveryAddressSchema),
  })

  const fetchAddresses = async () => {
    setLoading(true)
    try {
      const response = await api.get(
        `/organisations/${org.id}/delivery-addresses`
      )
      setAddresses(response.data.deliveryAddresses || response.data)
    } catch (error) {
      handleApiError(error, modal)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!org.id) return
    fetchAddresses()
  }, [org.id])

  const handleOpenModal = async () => {
    await preloadModal()
    setOpen(true)
  }

  const handleEdit = (address: DeliveryAddressInterface) => {
    setEditingId(address.id)
    reset({ address: address.address })
  }

  const handleCancel = () => {
    setEditingId('')
    reset()
  }

  const updateAddress = async (
    id: string,
    formData: DeliveryAddressFormType
  ) => {
    try {
      await api.put(`/organisations/${org.id}/delivery-addresses/${id}`, {
        address: formData.address,
      })
      setAddresses((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, address: formData.address } : item
        )
      )
      toast.success('Адрес обновлён')
      handleCancel()
    } catch (error) {
      handleApiError(error, modal)
    }
  }

  const handleSave = (id: string) =>
    handleSubmit((formData) => updateAddress(id, formData))()

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/organisations/${org.id}/delivery-addresses/${id}`)
      toast.success('Адрес удалён')
      fetchAddresses()
    } catch (error) {
      handleApiError(error, modal)
    }
  }

  return (
    <section
      className={classNames(
        cn.deliveryAddresses,
        isSelectedOrganisation && cn.selectedOrganisation
      )}
    >
      {contextHolder}
      <div className={cn.inner}>
        <div className={cn.creationButtonContainer}>
          <button onMouseEnter={preloadModal} onClick={handleOpenModal}>
            <div>
              <EnvironmentOutlined />
            </div>
            Создать адрес доставки
          </button>
        </div>

        {open && (
          <Suspense fallback={null}>
            <DeliveryAddressCreationModal
              open={open}
              setOpen={setOpen}
              onSuccess={fetchAddresses}
            />
          </Suspense>
        )}

        {loading ? (
          <Loader size={20} />
        ) : addresses.length > 0 ? (
          <div className={cn.table}>
            <Table
              dataSource={addresses}
              columns={getColumns({
                editingId,
                control,
                register,
                errors,
                onEdit: handleEdit,
                onSave: handleSave,
                onCancel: handleCancel,
                onDelete: handleDelete,
                isSelectedOrganisation,
              })}
              rowKey="id"
              scroll={{ x: 'max-content' }}
              pagination={{
                defaultPageSize: DEFAULT_PAGE_SIZE,
                showSizeChanger: getPageSizeChanger(
                  isSelectedOrganisation
                    ? cn.pageSizePopupSelected
                    : cn.pageSizePopup
                ),
              }}
            />
          </div>
        ) : (
          <p className={cn.emptyMessage}>Нет адресов доставки</p>
        )}
      </div>
    </section>
  )
}

export default DeliveryAddresses
